import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { ChromePicker } from 'react-color';
import * as Yup from 'yup';
import { parsePhoneNumber } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api, { getCsrfToken } from '../../../services/api';
import ThemeContext from '../../../ThemeContext';
import { URL_SEGMENTS } from '../../../../config/Routesconfig';

// Validation schemas
const nameSchema = Yup.string()
  .required('Please fill this field.')
  .min(3, 'Name must contain at least 3 letters.')
  .matches(/^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/, 'Name should not start or end with a number or special character.');

const emailSchema = Yup.string()
  .required('Please fill this field.')
  .email('Invalid email address.')
  .min(8, 'Email must be at least 8 characters long.');

const phoneSchema = Yup.string()
  .required('Mobile is required.')
  .test('valid-mobile', 'Invalid mobile number.', function (value) {
    if (!value) return false;
    try {
      const phoneToValidate = value.startsWith('+') ? value : `+${value}`;
      const phoneNumber = parsePhoneNumber(phoneToValidate);
      return phoneNumber && phoneNumber.isValid();
    } catch {
      return false;
    }
  });

const zipSchema = Yup.string()
  .required('Please fill this field.')
  .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits.');

const passwordSchema = Yup.string()
  .min(8, 'Password must be at least 8 characters.')
  .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
  .matches(/[a-z]/, 'Password must contain at least one lowercase letter.')
  .matches(/[0-9]/, 'Password must contain at least one number.')
  .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character.');

const basicSchema = Yup.object({
  client_name: nameSchema,
  start_date: Yup.date().nullable().required('Start Date is required.'),
  primary_name: nameSchema,
  primary_email: emailSchema,
  primary_phone: phoneSchema,
  country: Yup.string().required('Country is required.'),
  address: Yup.string().required('Address is required.'),
  city: Yup.string().required('City is required.'),
  state: Yup.string().required('State is required.'),
  zip: zipSchema,
});

const adminSchema = Yup.object({
  first_name: nameSchema,
  last_name: nameSchema,
  email: emailSchema,
  username: Yup.string().required('Please fill this field.'),
  password: passwordSchema.nullable().optional(),
  password_confirmation: Yup.string().when('password', {
    is: (password) => !!password && password.length > 0,
    then: (schema) =>
      schema
        .required('Please confirm the password.')
        .oneOf([Yup.ref('password'), null], 'Passwords do not match.'),
    otherwise: (schema) => schema.nullable().optional(),
  }),
});

const validatePassword = (value, isTyping = false) => {
  if (!value || value.length === 0) return '';
  if (isTyping) {
    if (value.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter.';
    if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter.';
    if (!/[0-9]/.test(value)) return 'Password must contain at least one number.';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Password must contain at least one special character.';
  }
  return '';
};

const validateConfirmation = (pw, conf) => {
  if (!pw || pw.length === 0) return '';
  if (!conf) return 'Please confirm your password.';
  if (conf !== pw) return 'Passwords do not match.';
  return '';
};

const EditEnterpriseClient = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const today = new Date();

  const [formData, setFormData] = useState({
    client_name: '',
    start_date: today.toISOString().split('T')[0],
    location: '',
    primary_name: '',
    primary_email: '',
    primary_phone: '',
    country: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    enterprise_client_description: '',
    text_color: '#000000',
    banner_color: '#ffffff',
    enterprise_client_logo: null,
    url: '',
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    password_confirmation: '',
    reseller_id: '',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [isReseller, setIsReseller] = useState(currentUser.role === 'reseller');
  const [isSuperAdmin, setIsSuperAdmin] = useState(currentUser.role === 'superadmin');
  const [isAdmin, setIsAdmin] = useState(currentUser.role === 'admin');
  const [isLoading, setIsLoading] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBannerColorPicker, setShowBannerColorPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [checking, setChecking] = useState({ email: false, mobile: false });
  const [phoneInputKey, setPhoneInputKey] = useState(0);
  const [selectedDate, setSelectedDate] = useState(today);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [logoName, setLogoName] = useState('No file chosen');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

  const textColorPickerRef = useRef(null);
  const bannerColorPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const iconRef = useRef(null);
  const calendarRef = useRef(null);

  let hideTimer = null;

  const clearHideTimer = () => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const startHideTimer = () => {
    clearHideTimer();
    hideTimer = setTimeout(() => {
      setShowDatePicker(false);
    }, 150);
  };

  const handleIconMouseEnter = () => {
    clearHideTimer();
    setShowDatePicker(true);
  };

  const handleIconMouseLeave = () => {
    startHideTimer();
  };

  const handleCalendarMouseEnter = () => {
    clearHideTimer();
    setShowDatePicker(true);
  };

  const handleCalendarMouseLeave = () => {
    startHideTimer();
  };

  const handleDateIconClick = () => {
    clearHideTimer();
    setShowDatePicker((prev) => !prev);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormData((prev) => {
      const updated = { ...prev, start_date: date.toISOString().split('T')[0] };
      try {
        basicSchema.validateSyncAt('start_date', updated);
        setErrors((prevE) => ({ ...prevE, start_date: '' }));
      } catch (err) {
        setErrors((prevE) => ({ ...prevE, start_date: err.message }));
      }
      return updated;
    });
    setTouched((prev) => ({ ...prev, start_date: true }));
    setShowDatePicker(false);
  };

  // Determine redirect path based on role
  let redirectPath = '/';
  if (currentUser.role === 'superadmin') {
      redirectPath = `/${URL_SEGMENTS.superadmin}/${URL_SEGMENTS.enterpriseClients}`;
  } else if (currentUser.role === 'admin') {
      redirectPath = `/${URL_SEGMENTS.admin}/${URL_SEGMENTS.enterpriseClients}`;
  } else if (currentUser.role === 'reseller') {
      redirectPath = `/${currentUser.username}/${URL_SEGMENTS.enterpriseClients}`;
  }

  useEffect(() => {
    if (!['reseller', 'superadmin', 'admin'].includes(currentUser.role)) {
      toast.error('Unauthorized access');
      navigate(redirectPath, { replace: true });
      return;
    }

    setIsReseller(currentUser.role === 'reseller');
    setIsSuperAdmin(currentUser.role === 'superadmin');
    setIsAdmin(currentUser.role === 'admin');
  }, [currentUser.role, navigate]);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const fetchCountries = debounce(async () => {
    try {
      const response = await fetch('https://countriesnow.space/api/v0.1/countries');
      const data = await response.json();
      if (data.error) throw new Error('Failed to fetch countries');
      setCountries(data.data.map((country) => country.country));
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries(['United States', 'Canada', 'Other']);
    }
  }, 500);

  const fetchStates = async (country) => {
    if (!country) {
      setStatesList([]);
      return;
    }
    try {
      const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country }),
      });
      const data = await response.json();
      if (data.error || !data.data) throw new Error(data.msg || 'Failed to fetch states');
      const states = data.data.states || [];
      setStatesList(states.map((state) => state.name));
    } catch (error) {
      console.error('Error fetching states:', error);
      setStatesList([]);
    }
  };

  useEffect(() => {
    fetchCountries();

    const fetchCsrf = async () => {
      try {
        await getCsrfToken();
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
        toast.error('Failed to fetch CSRF token.');
      }
    };
    fetchCsrf();

    const fetchClientData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/enterprise-clients/${id}`);
        const client = response.data;
        const startDate = client.start_date ? new Date(client.start_date) : today;
        const logoUrl = client.enterprise_client_logo
          ? client.enterprise_client_logo.startsWith('http')
            ? client.enterprise_client_logo
            : `${process.env.REACT_APP_BACKEND_URL || ''}${client.enterprise_client_logo}`
          : null;

        setSelectedDate(startDate);
        setFormData({
          client_name: client.client_name || '',
          start_date: startDate.toISOString().split('T')[0],
          location: client.location || '',
          primary_name: client.primary_name || '',
          primary_email: client.primary_email || '',
          primary_phone: client.primary_phone || '',
          country: client.country || '',
          address: client.address || '',
          city: client.city || '',
          state: client.state || '',
          zip: client.zip || '',
          enterprise_client_description: client.enterprise_client_description || '',
          text_color: client.text_color || '#000000',
          banner_color: client.banner_color || '#ffffff',
          enterprise_client_logo: null,
          url: client.url || '',
          first_name: client.first_name || '',
          last_name: client.last_name || '',
          email: client.email || client.primary_email || '',
          username: client.username || '',
          password: '',
          password_confirmation: '',
          reseller_id: client.reseller_id || currentUser.id,
        });
        setLogoName(client.enterprise_client_logo ? 'Current logo' : 'No file chosen');
        setLogoPreviewUrl(logoUrl);
      } catch (error) {
        console.error('Error fetching enterprise client:', error);
        toast.error(displayText('Failed to fetch enterprise client data.'));
        navigate(redirectPath);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [id, currentUser.id, navigate]);

  useEffect(() => {
    if (formData.country) {
      fetchStates(formData.country);
    } else {
      setStatesList([]);
      setFormData((prev) => ({ ...prev, state: '' }));
    }
  }, [formData.country]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (textColorPickerRef.current && !textColorPickerRef.current.contains(event.target)) {
        setShowTextColorPicker(false);
      }
      if (bannerColorPickerRef.current && !bannerColorPickerRef.current.contains(event.target)) {
        setShowBannerColorPicker(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };
      if (name === 'primary_email') updatedFormData.email = value;
      if (name === 'email') updatedFormData.primary_email = value;
      return updatedFormData;
    });

    if (value) setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === 'password' && !value) {
      setErrors((prev) => ({ ...prev, password: '', password_confirmation: '' }));
      return;
    }
    if (name === 'password_confirmation' && !formData.password && !value) {
      setErrors((prev) => ({ ...prev, password_confirmation: '' }));
      return;
    }

    try {
      const schemaToUse = ['first_name', 'last_name', 'email', 'username', 'password', 'password_confirmation'].includes(name)
        ? adminSchema
        : basicSchema;
      await schemaToUse.validateAt(name, { ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: '' }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }

    if (name === 'password' && isPasswordFocused && value) {
      const pwError = validatePassword(value, true);
      setErrors((prev) => ({ ...prev, password: pwError }));
      const confError = validateConfirmation(value, formData.password_confirmation);
      setErrors((prev) => ({ ...prev, password_confirmation: confError }));
    } else if (name === 'password_confirmation' && formData.password) {
      const confError = validateConfirmation(formData.password, value);
      setErrors((prev) => ({ ...prev, password_confirmation: confError }));
    }
  };

  const handleFocus = (e) => {
    if (e.target.name === 'password') setIsPasswordFocused(true);
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    if (value) setTouched((prev) => ({ ...prev, [name]: true }));

    if (!value && ['client_name', 'start_date', 'primary_name', 'primary_email', 'country', 'address', 'city', 'state', 'zip', 'first_name', 'last_name', 'email', 'username'].includes(name)) {
      setErrors((prev) => ({ ...prev, [name]: 'Please fill this field.' }));
      return;
    }

    if (name === 'password' && !value) {
      setErrors((prev) => ({ ...prev, password: '', password_confirmation: '' }));
      setIsPasswordFocused(false);
      return;
    }

    try {
      const schemaToUse = ['first_name', 'last_name', 'email', 'username', 'password', 'password_confirmation'].includes(name)
        ? adminSchema
        : basicSchema;
      await schemaToUse.validateAt(name, { ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: '' }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }

    if (name === 'password' && value) {
      setIsPasswordFocused(false);
      const pwError = validatePassword(value);
      setErrors((prev) => ({ ...prev, password: pwError }));
      const confError = validateConfirmation(value, formData.password_confirmation);
      setErrors((prev) => ({ ...prev, password_confirmation: confError }));
    } else if (name === 'password_confirmation' && formData.password) {
      const confError = validateConfirmation(formData.password, value);
      setErrors((prev) => ({ ...prev, password_confirmation: confError }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, enterprise_client_logo: 'The logo must be an image (JPEG, PNG, GIF, BMP, or WebP).' }));
        setFormData((prev) => ({ ...prev, enterprise_client_logo: null }));
        setLogoName('No file chosen');
        setLogoPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
        return;
      }
      if (file.size > 2048 * 1024) {
        setErrors((prev) => ({ ...prev, enterprise_client_logo: 'The logo must not exceed 2MB.' }));
        setFormData((prev) => ({ ...prev, enterprise_client_logo: null }));
        setLogoName('No file chosen');
        setLogoPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
        return;
      }
      setFormData((prev) => ({ ...prev, enterprise_client_logo: file }));
      setLogoName(file.name);
      setLogoPreviewUrl(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, enterprise_client_logo: '' }));
    } else {
      setFormData((prev) => ({ ...prev, enterprise_client_logo: null }));
      setLogoName('No file chosen');
      setLogoPreviewUrl(null);
      setErrors((prev) => ({ ...prev, enterprise_client_logo: '' }));
    }
  };

  const toggleTextColorPicker = () => {
    setShowTextColorPicker((prev) => !prev);
    if (showBannerColorPicker) setShowBannerColorPicker(false);
  };

  const toggleBannerColorPicker = () => {
    setShowBannerColorPicker((prev) => !prev);
    if (showTextColorPicker) setShowTextColorPicker(false);
  };

  const validateTab = async (tab) => {
    try {
      const schema = tab === 'basic' ? basicSchema : adminSchema;
      await schema.validate(formData, { abortEarly: false });
      return true;
    } catch (error) {
      const newErrors = {};
      error.inner.forEach((err) => {
        if ((err.path === 'password' || err.path === 'password_confirmation') && !formData[err.path]) return;
        newErrors[err.path] = err.message;
      });
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
  };

  const validateForm = async () => {
    try {
      await basicSchema.validate(formData, { abortEarly: false });
      await adminSchema.validate(formData, { abortEarly: false });
      setErrors((prev) => ({ ...prev, password: '', password_confirmation: '' }));
      return true;
    } catch (error) {
      const newErrors = {};
      error.inner.forEach((err) => {
        if ((err.path === 'password' || err.path === 'password_confirmation') && !formData[err.path]) return;
        newErrors[err.path] = err.message;
      });
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fill all required fields correctly.');
      return;
    }

    setIsLoading(true);
    try {
      const originalData = (await api.get(`/enterprise-clients/${id}`)).data;
      const payload = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === 'enterprise_client_logo' && formData[key] instanceof File) {
          payload.append('enterprise_client_logo', formData[key]);
        } else if (
          formData[key] !== originalData[key] &&
          formData[key] !== '' &&
          formData[key] != null &&
          key !== 'password' &&
          key !== 'password_confirmation'
        ) {
          payload.append(key, formData[key]);
        }
      });

      if (formData.password && formData.password.length > 0) {
        const pwError = validatePassword(formData.password);
        const confError = validateConfirmation(formData.password, formData.password_confirmation);
        if (pwError || confError) {
          setErrors((prev) => ({ ...prev, password: pwError, password_confirmation: confError }));
          setIsLoading(false);
          toast.error('Please correct password fields.');
          return;
        }
        payload.append('password', formData.password);
        payload.append('password_confirmation', formData.password_confirmation);
      }

      payload.append('_method', 'PUT');

      const response = await api.post(`/enterprise-clients/${id}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(response.data.message || displayText('Enterprise client updated successfully.'));
      setTimeout(() => navigate(redirectPath), 3000);
    } catch (error) {
      console.error('Error updating enterprise client:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update enterprise client.';
      toast.error(errorMessage);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(redirectPath);
  };

  const handleNext = async () => {
    if (activeTab === 'basic' && !(await validateTab('basic'))) {
      toast.error('Please fill all required fields correctly.');
      return;
    }
    if (activeTab === 'basic') {
      setActiveTab('admin');
      setErrors({});
    }
  };

  const handlePrevious = () => {
    if (activeTab === 'admin') {
      setActiveTab('basic');
    }
    setErrors({});
  };

  return (
    <div>
      <style>
        {`
          .color-picker-container { position: relative; display: inline-block; }
          .color-picker { position: absolute; top: 0; left: 100%; margin-left: 10px; z-index: 10; }
          .date-input-container {
            position: relative;
            border: 1px solid ${errors.start_date && touched.start_date ? theme.colors.danger : theme.colors.borderColor};
            border-radius: 0.25rem;
            padding: 0.375rem 0.75rem;
            background-color: #fff;
            height: 35px;
          }
          .date-input-container input { border: none; width: 100%; padding: 0; background: transparent; font-size: 12px; color: #666; height: 100%; line-height: 1.5; }
          .date-input-container i { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); color: ${theme.colors.textPrimary}; cursor: pointer; }
          .date-picker-popup { position: absolute; top: 100%; left: 0; z-index: 10; }
          select:focus, select:active { outline: none !important; box-shadow: none !important; border: 1px solid #000000 !important; }
        `}
      </style>

      <div className="breadcrumb-container">
        <div className="breadcrumb">
          <span style={{ color: theme.colors.primary }}>
            <Link to={redirectPath} style={{ color: theme.colors.primary, textDecoration: 'underline' }}>{displayText(" Client Management ")}</Link>
          </span>
          <span className="breadcrumb-divider">&gt;</span>
          <span style={{ color: theme.colors.textPrimary }}>{displayText("Edit Enterprise Client")}</span>
        </div>
      </div>

      <div className="edit-enterprise-client-container" style={{ backgroundColor: '#ffffff', padding: '1rem', borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', marginTop: '10px' }}>
        <ToastContainer position="top-right" autoClose={3000} />
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner" style={{ border: `4px solid ${theme.colors.primary}`, borderTop: '4px solid transparent' }} />
          </div>
        )}
        <h4 className="page-title" style={{ color: theme.colors.textPrimary }}>{displayText("Edit Enterprise Client")}</h4>
        <div className="tab-content-container" style={{ padding: '1rem', backgroundColor: '#fff', border: `1px solid ${theme.colors.borderColor}` }}>
          <div className="tab-controls">
            {['basic', 'admin'].map((tab, index) => (
              <a
                key={tab}
                href="#"
                className="tab-link"
                style={{ color: activeTab === tab ? theme.colors.primary : theme.colors.secondary }}
                onClick={async (e) => {
                  e.preventDefault();
                  if (activeTab === 'basic' && tab === 'admin' && !(await validateTab('basic'))) {
                    toast.error('Please fill all required fields correctly.');
                    return;
                  }
                  setActiveTab(tab);
                  setErrors({});
                }}
              >
                <span className="tab-indicator" style={{
                  border: `2px solid ${activeTab === tab ? theme.colors.primary : theme.colors.secondary}`,
                  color: activeTab === tab ? theme.colors.textWhite : theme.colors.secondary,
                  backgroundColor: activeTab === tab ? theme.colors.primary : 'transparent',
                }}>
                  {index + 1}
                </span>
                <span className="tab-label" style={{ borderBottom: activeTab === tab ? `2px solid ${theme.colors.primary}` : 'none' }}>
                  {tab === 'basic' ? 'Basic Information' : 'Admin Setup'}
                </span>
              </a>
            ))}
          </div>
          {activeTab === 'basic' && (
            <div className="tab-content">
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>{displayText(" Client Name* ")}</label>
                  <input
                    type="text"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.client_name && touched.client_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.client_name && touched.client_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.client_name && touched.client_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.client_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Start Date*
                  </label>
                  <div
                    className="date-input-container"
                    ref={datePickerRef}
                  >
                    <input
                      type="text"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`form-control ${errors.start_date && touched.start_date ? 'is-invalid' : ''}`}
                      style={{
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        fontSize: '12px',
                        fontFamily: 'inherit',
                        fontWeight: '100',
                      }}
                      required
                    />
                    <span
                      ref={iconRef}
                      onMouseEnter={handleIconMouseEnter}
                      onMouseLeave={handleIconMouseLeave}
                      onClick={handleDateIconClick}
                    >
                      <i className="bi bi-calendar" />
                    </span>
                    {showDatePicker && (
                      <div
                        className="date-picker-popup"
                        ref={calendarRef}
                        onMouseEnter={handleCalendarMouseEnter}
                        onMouseLeave={handleCalendarMouseLeave}
                      >
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          dateFormat="yyyy-MM-dd"
                          inline
                          className="custom-date-picker"
                        />
                      </div>
                    )}
                  </div>
                  {errors.start_date && touched.start_date && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.start_date}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="form-control"
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Primary Contact (Name)*
                  </label>
                  <input
                    type="text"
                    name="primary_name"
                    value={formData.primary_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.primary_name && touched.primary_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.primary_name && touched.primary_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.primary_name && touched.primary_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.primary_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Primary Contact (Email)*
                  </label>
                  <input
                    type="email"
                    name="primary_email"
                    value={formData.primary_email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.primary_email && touched.primary_email ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.primary_email && touched.primary_email ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.primary_email && touched.primary_email && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.primary_email}
                    </span>
                  )}
                  {checking.email && (
                    <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.3rem', fontSize: '0.875em' }}>
                      Checking availability...
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Primary Contact (Mobile)*
                  </label>
                  <PhoneInput
                    key={phoneInputKey}
                    country={'us'}
                    value={formData.primary_phone}
                    onChange={(value) => {
                      const phoneWithPrefix = value.startsWith('+') ? value : `+${value}`;
                      setFormData((prev) => ({ ...prev, primary_phone: phoneWithPrefix }));
                      setTouched((prev) => ({ ...prev, primary_phone: true }));
                    }}
                    onBlur={async () => {
                      const phoneValue = formData.primary_phone;
                      setTouched((prev) => ({ ...prev, primary_phone: true }));
                      if (!phoneValue) {
                        setErrors((prev) => ({ ...prev, primary_phone: 'Mobile is required.' }));
                        return;
                      }
                      try {
                        const phoneToValidate = phoneValue.startsWith('+') ? phoneValue : `+${phoneValue}`;
                        const phoneNumber = parsePhoneNumber(phoneToValidate);
                        if (!phoneNumber || !phoneNumber.isValid()) {
                          setErrors((prev) => ({ ...prev, primary_phone: 'Invalid mobile number.' }));
                          return;
                        }
                        setErrors((prev) => ({ ...prev, primary_phone: '' }));
                        setChecking((prev) => ({ ...prev, mobile: true }));
                        const response = await api.post('/check-phone', { phone: phoneToValidate });
                        if (response.data.exists) {
                          setErrors((prev) => ({ ...prev, primary_phone: 'Phone number is already taken.' }));
                        } else {
                          setErrors((prev) => ({ ...prev, primary_phone: '' }));
                        }
                        setChecking((prev) => ({ ...prev, mobile: false }));
                      } catch (error) {
                        setErrors((prev) => ({ ...prev, primary_phone: error.response?.data?.message || 'Invalid mobile number.' }));
                        setChecking((prev) => ({ ...prev, mobile: false }));
                      }
                    }}
                    inputClass={`form-control ${errors.primary_phone && touched.primary_phone ? 'is-invalid' : ''}`}
                    inputStyle={{
                      width: '100%',
                      height: '35px',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                      backgroundColor: theme.colors.bgPrimary,
                      border: `1px solid ${errors.primary_phone && touched.primary_phone ? theme.colors.danger : theme.colors.borderColor}`,
                      borderRadius: '4px',
                      color: '#666',
                      padding: '6px 10px 6px 45px',
                    }}
                    containerStyle={{ width: '100%' }}
                    inputProps={{
                      name: 'primary_phone',
                      required: true,
                    }}
                    buttonStyle={{
                      border: `1px solid ${theme.colors.borderColor}`,
                      backgroundColor: theme.colors.bgPrimary,
                      borderRight: 'none',
                      borderRadius: '4px 0 0 4px',
                      height: '35px',
                    }}
                  />
                  {errors.primary_phone && touched.primary_phone && (
                    <div
                      className="invalid-feedback d-block"
                      style={{
                        color: theme.colors.danger,
                        display: 'block',
                        width: '100%',
                        marginTop: '0.25rem',
                        fontSize: '0.875em',
                      }}
                    >
                      {errors.primary_phone}
                    </div>
                  )}
                  {checking.mobile && (
                    <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.25rem', fontSize: '0.875em' }}>
                      Checking availability...
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Country*
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.country && touched.country ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.country && touched.country ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  {errors.country && touched.country && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.country}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Address*
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.address && touched.address ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.address && touched.address ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.address && touched.address && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.address}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    City*
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.city && touched.city ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.city && touched.city ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.city && touched.city && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.city}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    State*
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={!formData.country}
                    className={`form-control ${errors.state && touched.state ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.state && touched.state ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  >
                    <option value="">Select State</option>
                    {statesList.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.state && touched.state && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.state}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Zip*
                  </label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.zip && touched.zip ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.zip && touched.zip ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.zip && touched.zip && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.zip}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>{displayText(" Enterprise Client Description ")}</label>
                  <textarea
                    name="enterprise_client_description"
                    value={formData.enterprise_client_description}
                    onChange={handleInputChange}
                    rows="2"
                    className="form-control"
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Website Look & Feel
                  </label>
                  <div
                    style={{
                      backgroundColor: formData.banner_color || '#ffffff',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: `1px solid ${theme.colors.borderColor}`,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                  >
                    {logoPreviewUrl && (
                      <img
                        src={logoPreviewUrl}
                        alt="Logo Preview"
                        style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'contain' }}
                        onError={() => setLogoPreviewUrl(null)}
                      />
                    )}
                    <div>
                      <p
                        style={{
                          color: formData.text_color || '#000000',
                          marginBottom: '1rem',
                          fontSize: '12px',
                        }}
                      >
                        Sample Text
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <div className="color-picker-container">
                          <button
                            style={{
                              padding: '0.4rem',
                              fontSize: '12px',
                              borderRadius: '4px',
                              border: 'none',
                              background: showTextColorPicker
                                ? theme.gradients.primary
                                : theme.gradients.secondary,
                              color: theme.colors.textWhite,
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease, opacity 0.3s ease',
                              minWidth: '120px',
                            }}
                            onClick={toggleTextColorPicker}
                            onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                            onMouseOut={(e) => (e.target.style.opacity = '1')}
                          >
                            Change Text Color
                          </button>
                          {showTextColorPicker && (
                            <div
                              ref={textColorPickerRef}
                              className="color-picker"
                            >
                              <ChromePicker
                                color={formData.text_color || '#000000'}
                                onChange={(color) => setFormData({ ...formData, text_color: color.hex })}
                                styles={{ default: { picker: { width: '180px' } } }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="color-picker-container">
                          <button
                            style={{
                              padding: '0.4rem',
                              fontSize: '12px',
                              borderRadius: '4px',
                              border: 'none',
                              background: showBannerColorPicker
                                ? theme.gradients.primary
                                : theme.gradients.secondary,
                              color: theme.colors.textWhite,
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease, opacity 0.3s ease',
                              minWidth: '120px',
                            }}
                            onClick={toggleBannerColorPicker}
                            onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                            onMouseOut={(e) => (e.target.style.opacity = '1')}
                          >
                            Change Banner Color
                          </button>
                          {showBannerColorPicker && (
                            <div
                              ref={bannerColorPickerRef}
                              className="color-picker"
                            >
                              <ChromePicker
                                color={formData.banner_color || '#ffffff'}
                                onChange={(color) => setFormData({ ...formData, banner_color: color.hex })}
                                styles={{ default: { picker: { width: '180px' } } }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>{displayText(" Enterprise Client Logo ")}</label>
                  <div
                    className="file-input-container"
                    style={{
                      border: `1px solid ${errors.enterprise_client_logo ? theme.colors.danger : theme.colors.borderColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.25rem',
                      backgroundColor: '#fff',
                    }}
                  >
                    <label
                      htmlFor="logo-upload"
                      style={{
                        background: theme.gradients.primary,
                        color: theme.colors.textWhite,
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        borderRadius: '0.25rem',
                        marginLeft: '-8px',
                        marginRight: '10px',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                      onMouseOut={(e) => (e.target.style.opacity = '1')}
                    >
                      Browse
                    </label>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoName}</span>
                  </div>
                  <input
                    type="file"
                    id="logo-upload"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  {errors.enterprise_client_logo && (
                    <span className="error-text" style={{ color: theme.colors.danger }}>
                      {errors.enterprise_client_logo}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Website
                  </label>
                  <input
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    className="form-control"
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="action-button"
                  style={{ background: theme.gradients.secondary, color: theme.colors.textWhite }}
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  className="action-button"
                  style={{ background: theme.gradients.primary, color: theme.colors.textWhite }}
                  onClick={handleNext}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {activeTab === 'admin' && (
            <div className="tab-content">
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    First Name*
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.first_name && touched.first_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.first_name && touched.first_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.first_name && touched.first_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.first_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Last Name*
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.last_name && touched.last_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.last_name && touched.last_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.last_name && touched.last_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.last_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Email*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.email && touched.email ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.email && touched.email && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.email}
                    </span>
                  )}
                  {checking.email && (
                    <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.3rem', fontSize: '0.875em' }}>
                      Checking availability...
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Username*
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.username && touched.username ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.username && touched.username ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.username && touched.username && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.username}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.password && touched.password ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                  {errors.password && touched.password && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.password}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Retype New Password
                  </label>
                  <input
                    type="password"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.password_confirmation && touched.password_confirmation ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.password_confirmation && touched.password_confirmation ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                  {errors.password_confirmation && touched.password_confirmation && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.password_confirmation}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="action-button"
                  style={{ background: theme.gradients.secondary, color: theme.colors.textWhite }}
                  onClick={handlePrevious}
                >
                  Previous
                </button>
                <button
                  className="action-button"
                  style={{ background: theme.gradients.secondary, color: theme.colors.textWhite }}
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  className="action-button"
                  style={{ background: theme.gradients.primary, color: theme.colors.textWhite, opacity: isLoading ? 0.6 : 1 }}
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditEnterpriseClient;